import { useEffect, useRef, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  HOTELS_QUERY_KEY,
  getHotel,
  updateHotel,
  uploadMedia,
  type Hotel,
} from '../features/hotels/api.js';
import { HotelContactsPanel } from '../features/hotels/hotel-contacts-panel.js';
import { NotificationRecipientsPanel } from '../features/hotels/notification-recipients-panel.js';
import { ApprovalReminderPanel } from '../features/hotels/approval-reminder-panel.js';
import { Field, FormActions } from '../components/ui/form.js';
import { Input } from '../components/ui/input.js';
import { Button } from '../components/ui/button.js';
import { slugify } from '../lib/utils.js';

function HotelSettingsPage() {
  const { hotelId } = Route.useParams();
  const qc = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const hotel = useQuery<Hotel>({
    queryKey: ['hotel', hotelId],
    queryFn: () => getHotel(hotelId),
  });

  // Local form state, hydrated from the loaded hotel. We keep the inputs
  // controlled so the dirty flag is honest — only enabled save when the
  // user actually changed something.
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [freq, setFreq] = useState(2);
  const [leadDays, setLeadDays] = useState(7);
  const [ratio, setRatio] = useState(0.7);
  const [coverImageUrl, setCoverImageUrl] = useState('');
  const [dirty, setDirty] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  useEffect(() => {
    if (!hotel.data) return;
    setName(hotel.data.name);
    setSlug(hotel.data.slug);
    setWebsiteUrl(hotel.data.websiteUrl ?? '');
    setFreq(hotel.data.defaultPostFrequencyPerWeek);
    setLeadDays(hotel.data.eventLeadDays ?? 7);
    setRatio(hotel.data.brandSalesRatio);
    setCoverImageUrl(hotel.data.coverImageUrl ?? '');
    setDirty(false);
  }, [hotel.data]);

  const save = useMutation({
    mutationFn: () => {
      const body: Parameters<typeof updateHotel>[1] = {
        name,
        slug,
        defaultPostFrequencyPerWeek: freq,
        eventLeadDays: leadDays,
        brandSalesRatio: ratio,
        coverImageUrl: coverImageUrl || null,
      };
      if (websiteUrl) body.websiteUrl = websiteUrl;
      return updateHotel(hotelId, body);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['hotel', hotelId] });
      void qc.invalidateQueries({ queryKey: HOTELS_QUERY_KEY });
      setDirty(false);
    },
  });

  // Image upload goes through the existing /hotels/:id/media endpoint
  // (tagged 'cover'), then we pin the returned URL on the hotel itself.
  const upload = useMutation({
    mutationFn: async (file: File) => {
      const media = await uploadMedia(hotelId, file, ['cover'], null);
      return media.url;
    },
    onSuccess: (url) => {
      setCoverImageUrl(url);
      setDirty(true);
      setUploadError(null);
    },
    onError: (err: Error) => setUploadError(err.message),
  });

  function onNameChange(v: string) {
    setName(v);
    if (!dirty && hotel.data && v !== hotel.data.name) setDirty(true);
    setDirty(true);
  }
  function track<T>(setter: (v: T) => void) {
    return (v: T) => {
      setter(v);
      setDirty(true);
    };
  }

  if (hotel.isLoading) return <p className="text-body text-[var(--fg-2)]">Betöltés…</p>;
  if (!hotel.data) return <p className="text-body text-[var(--danger)]">A hotel nem található.</p>;

  return (
    <div className="space-y-6">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
        className="space-y-6"
      >
        <div className="grid gap-6 lg:grid-cols-2">
          {/* LEFT: identity + content settings stacked */}
          <div className="space-y-6">
            <section className="space-y-5 rounded-lg border border-[var(--app-divider)] bg-[var(--app-surface)] p-5">
              <h2 className="text-h2 font-semibold text-[var(--ink-700)]">Alapadatok</h2>

              <Field label="Hotel név" htmlFor="name">
                <Input
                  id="name"
                  value={name}
                  required
                  onChange={(e) => onNameChange(e.target.value)}
                />
              </Field>

              <Field
                label="Azonosító (slug)"
                htmlFor="slug"
                hint="URL-barát, csak kisbetű / szám / kötőjel"
              >
                <div className="flex items-center gap-2">
                  <Input
                    id="slug"
                    value={slug}
                    required
                    pattern="[a-z0-9-]+"
                    onChange={(e) => track(setSlug)(e.target.value)}
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => track(setSlug)(slugify(name))}
                    title="Generálás a névből"
                  >
                    Generálás
                  </Button>
                </div>
              </Field>

              <Field
                label="Weboldal"
                htmlFor="website"
                hint="A weboldal beolvasása erre az URL-re fut"
              >
                <Input
                  id="website"
                  type="url"
                  value={websiteUrl}
                  placeholder="https://hotel.hu"
                  onChange={(e) => track(setWebsiteUrl)(e.target.value)}
                />
              </Field>
            </section>

            {/* Content settings — own card so the right-side cover panel can
              breathe. The numeric fields stack vertically here instead of
              the cramped 3-column grid we had on the right. */}
            <section className="space-y-5 rounded-lg border border-[var(--app-divider)] bg-[var(--app-surface)] p-5">
              <h2 className="text-h2 font-semibold text-[var(--ink-700)]">Tartalmi beállítások</h2>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field label="Posztgyakoriság" htmlFor="freq" hint="Hány posztot ütemezünk hetente">
                  <Input
                    id="freq"
                    type="number"
                    min={1}
                    max={14}
                    value={freq}
                    onChange={(e) => track(setFreq)(Number(e.target.value))}
                  />
                </Field>
                <Field
                  label="Esemény-promotion lead-time"
                  htmlFor="leadDays"
                  hint="Hány nappal egy esemény (ünnep / hotel program) előtt indítsa az AI a promotálást"
                >
                  <Input
                    id="leadDays"
                    type="number"
                    min={0}
                    max={30}
                    value={leadDays}
                    onChange={(e) => track(setLeadDays)(Number(e.target.value))}
                  />
                </Field>
              </div>
              <div>
                <div className="flex items-baseline justify-between">
                  <label htmlFor="ratio" className="text-meta font-medium text-[var(--ink-700)]">
                    Brand / Sales arány
                  </label>
                  <span className="font-mono text-meta tabular-nums text-[var(--fg-2)]">
                    <span className="font-semibold text-[var(--brand-700)]">
                      {Math.round(ratio * 100)}%
                    </span>
                    <span className="mx-1 text-[var(--fg-faint)]">brand</span>
                    <span className="text-[var(--fg-faint)]">·</span>
                    <span className="mx-1 text-[var(--fg-faint)]">sales</span>
                    <span className="font-semibold text-[var(--cat-sales-fg)]">
                      {Math.round((1 - ratio) * 100)}%
                    </span>
                  </span>
                </div>
                <input
                  id="ratio"
                  type="range"
                  min={0}
                  max={1}
                  step={0.05}
                  value={ratio}
                  onChange={(e) => track(setRatio)(Number(e.target.value))}
                  aria-label="Brand / Sales arány csúszka"
                  className="mt-3 h-2 w-full cursor-pointer appearance-none rounded-full focus:outline-none focus:ring-2 focus:ring-[var(--brand-500)]/40"
                  style={{
                    background: `linear-gradient(to right, var(--brand-500) 0%, var(--brand-500) ${Math.round(
                      ratio * 100,
                    )}%, var(--cat-sales-bg) ${Math.round(ratio * 100)}%, var(--cat-sales-bg) 100%)`,
                  }}
                />
                {/* Concrete weekly breakdown — easier to reason about than a
                  raw %. Uses freq × ratio rounded; floor + ceil so the
                  two halves always sum to freq exactly. */}
                {(() => {
                  const brandPosts = Math.round(freq * ratio);
                  const salesPosts = freq - brandPosts;
                  return (
                    <div className="mt-2 flex items-center justify-center gap-2 text-meta text-[var(--fg-2)]">
                      <span className="font-mono tabular-nums">
                        <strong className="text-[var(--brand-700)]">{brandPosts}</strong> brand
                      </span>
                      <span className="text-[var(--fg-faint)]">+</span>
                      <span className="font-mono tabular-nums">
                        <strong className="text-[var(--cat-sales-fg)]">{salesPosts}</strong> sales
                      </span>
                      <span className="text-[var(--fg-faint)]">/ hét</span>
                      <span className="text-[var(--fg-faint)]">·</span>
                      <span className="text-[var(--fg-faint)]">
                        {brandPosts * 4} + {salesPosts * 4} / hónap
                      </span>
                    </div>
                  );
                })()}
                <div className="mt-1.5 flex justify-between text-meta text-[var(--fg-faint)]">
                  <span>0 = csak sales</span>
                  <span>1 = csak brand</span>
                </div>
              </div>
            </section>
          </div>

          {/* RIGHT: cover image — focused, no longer fighting for space with the form */}
          <section className="space-y-5 rounded-lg border border-[var(--app-divider)] bg-[var(--app-surface)] p-5">
            <h2 className="text-h2 font-semibold text-[var(--ink-700)]">Címkép</h2>

            <Field
              label="Címkép URL"
              htmlFor="cover-url"
              hint="A weboldal beolvasása tölti ki automatikusan, de kézzel is felülírható"
            >
              <Input
                id="cover-url"
                type="url"
                value={coverImageUrl}
                placeholder="https://… vagy nyomd meg a Feltöltés gombot"
                onChange={(e) => track(setCoverImageUrl)(e.target.value)}
              />
            </Field>

            {/* Preview */}
            <div
              className="aspect-[5/3] w-full rounded-lg border border-[var(--app-divider)] bg-[var(--app-bg)]"
              style={{
                backgroundImage: coverImageUrl ? `url('${coverImageUrl}')` : undefined,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
              aria-label="Címkép előnézet"
            >
              {!coverImageUrl && (
                <div className="flex h-full items-center justify-center text-meta text-[var(--fg-faint)]">
                  Nincs címkép — futtasd a crawl-t, vagy tölts fel egy képet
                </div>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) upload.mutate(f);
                  e.target.value = '';
                }}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={upload.isPending}
              >
                {upload.isPending ? 'Feltöltés…' : '📁 Kép feltöltése'}
              </Button>
              {coverImageUrl && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setCoverImageUrl('');
                    setDirty(true);
                  }}
                >
                  Címkép törlése
                </Button>
              )}
              {uploadError && <span className="text-meta text-[var(--danger)]">{uploadError}</span>}
            </div>
          </section>
        </div>

        {save.isError && (
          <p className="text-body text-[var(--danger)]">
            Mentési hiba: {(save.error as Error).message}
          </p>
        )}

        <FormActions>
          {dirty && (
            <span className="mr-2 text-meta text-[var(--fg-2)]">
              Nem mentett változások vannak.
            </span>
          )}
          <Button type="submit" disabled={save.isPending || !dirty}>
            {save.isPending ? 'Mentés…' : 'Mentés'}
          </Button>
        </FormActions>
      </form>

      <HotelContactsPanel hotelId={hotelId} />
      <NotificationRecipientsPanel hotelId={hotelId} />
      <ApprovalReminderPanel hotelId={hotelId} />
    </div>
  );
}

export const Route = createFileRoute('/app/hotels/$hotelId/settings')({
  component: HotelSettingsPage,
});
